/**
 * Roles Table Skeleton Component
 *
 * Skeleton loader displayed while roles data is loading.
 * Shows animated placeholder rows matching the actual table structure.
 *
 * @module domains/personnel/presentation/components/list/RolesTableSkeleton
 */

import { RolesTableHeader } from './RolesTableHeader';

/**
 * Roles Table Skeleton Component
 *
 * Displays skeleton loading state for the roles table.
 * Shows header with column labels and 10 animated skeleton rows.
 *
 * @returns {React.ReactElement} Skeleton loader component
 *
 * @example
 * {isLoading && <RolesTableSkeleton />}
 */
const styles = {
  container: 'border border-border rounded-lg w-full',
  row: {
    container: 'flex items-center px-2 py-4 border-b border-border last:border-b-0',
    column: 'flex-1 px-2',
    columnRight: 'flex-1 px-2 flex justify-end',
  },
  skeleton: {
    base: 'bg-muted rounded animate-pulse',
    roleName: 'h-4 w-48', // Skeleton for role name
    usersCount: 'h-4 w-32', // Skeleton for users count
    actions: 'h-4 w-12', // Skeleton for actions button
  },
};

export const RolesTableSkeleton = (): React.ReactElement => {
  return (
    <div className={styles.container}>
      {/* Table Header */}
      <RolesTableHeader />

      {/* Skeleton Rows */}
      <div>
        {Array.from({ length: 10 }).map((_, index) => (
          <div className={styles.row.container} key={index}>
            {/* Role Name Skeleton */}
            <div className={styles.row.column}>
              <div className={`${styles.skeleton.base} ${styles.skeleton.roleName}`} />
            </div>

            {/* Users Count Skeleton */}
            <div className={styles.row.columnRight}>
              <div className={`${styles.skeleton.base} ${styles.skeleton.usersCount}`} />
            </div>

            {/* Actions Skeleton */}
            <div className={styles.row.columnRight}>
              <div className={`${styles.skeleton.base} ${styles.skeleton.actions}`} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
